import { useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { Box, Typography, Tooltip, FormHelperText } from "@mui/material";
import {
  DeleteOutlined as DeleteIcon,
  PreviewOutlined as PreviewIcon,
  FileUploadOutlined as UploadIcon,
  PictureAsPdfOutlined as PdfIcon,
  ImageOutlined as ImageIcon,
  DescriptionOutlined as DocIcon,
  MailOutline as MailIcon,
  AttachEmailOutlined as EmailIcon,
  InsertDriveFileOutlined as GenericFileIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import DSButton from "@accredo/fe-shared/theme/components/Button.jsx";
import { componentTokens } from "@accredo/fe-shared/theme/tokens/components.js";
import { dimensions, typographyTokens } from "@accredo/fe-shared/theme/index.js";
import DocumentViewer from "../MDDForm/Viewer/DocumentViewer.jsx";

const EXTENSION_MIME_MAP = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  tif: "image/tiff",
  tiff: "image/tiff",
  msg: "application/vnd.ms-outlook",
  eml: "message/rfc822",
};

const getMimeType = (file) => {
  if (file.type && file.type !== "application/octet-stream") return file.type;
  const ext = file.name.toLowerCase().split(".").pop();
  return EXTENSION_MIME_MAP[ext] || "application/octet-stream";
};

const getFileType = (filename) => {
  const lower = filename.toLowerCase().trim();
  if (lower.endsWith(".pdf")) return "PDF";
  if ([".png", ".jpg", ".jpeg", ".tif", ".tiff"].some((e) => lower.endsWith(e))) return "Image";
  if (lower.endsWith(".msg")) return "MSG";
  if (lower.endsWith(".eml")) return "EML";
  return "Download";
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const kb = bytes / 1024;
  const mb = kb / 1024;
  if (mb >= 1) return `${mb.toFixed(1).replace(".", ",")} MB`;
  return `${Math.round(kb).toLocaleString("de-DE")} KB`;
};

const getFileIcon = (filetype) => {
  switch (filetype) {
    case "PDF": return <PdfIcon fontSize="small" />;
    case "Image": return <ImageIcon fontSize="small" />;
    case "DOC": return <DocIcon fontSize="small" />;
    case "MSG": return <MailIcon fontSize="small" />;
    case "EML": return <EmailIcon fontSize="small" />;
    default: return <GenericFileIcon fontSize="small" />;
  }
};

/**
 * A drag-and-drop file upload component integrated with react-hook-form.
 * Stores file data in the form state for processing during parent form submission.
 *
 * Form value format: { id, filename, filesize, mimetype, filetype, filedata, label }[]
 *
 * @param {string} name - Form field name
 * @param {string} [label] - Optional label shown above the drop zone
 * @param {string[]} [allowedExtensions] - Allowed file extensions (e.g. ["pdf"])
 * @param {boolean} [disabled]
 * @param {boolean} [multiple] - Allow multiple files (default: false)
 */
const FormDnDFileUpload = ({
  name,
  label,
  allowedExtensions = [],
  disabled = false,
  multiple = false,
}) => {
  const { control } = useFormContext();
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, control });

  const [dragging, setDragging] = useState(false);
  const [documentToView, setDocumentToView] = useState(null);
  const fileInputRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const files = value || [];

  const isExtensionAllowed = (filename) => {
    if (!allowedExtensions.length) return true;
    const ext = filename.split(".").pop().toLowerCase();
    return allowedExtensions.map((e) => e.toLowerCase()).includes(ext);
  };

  const processFiles = async (rawFiles) => {
    let newFiles = multiple ? [...files] : [];

    const filePromises = rawFiles.map(
      (rawFile) =>
        new Promise((resolve, reject) => {
          if (!isExtensionAllowed(rawFile.name)) {
            enqueueSnackbar(
              `Ungültige Dateiendung '${rawFile.name.split(".").pop().toLowerCase()}' – Erlaubt: ${allowedExtensions.join(", ")}`,
              { variant: "error" }
            );
            reject();
            return;
          }

          const reader = new FileReader();
          reader.onload = (e) => {
            if (!multiple) newFiles = [];
            newFiles.push({
              id: uuidv4(),
              filename: rawFile.name,
              filesize: rawFile.size,
              mimetype: getMimeType(rawFile),
              filetype: getFileType(rawFile.name),
              filedata: e.target.result, // base64 data URL — used for local preview only
              originalFile: rawFile,     // original File reference — used for actual upload
              label: rawFile.name,
            });
            resolve();
          };
          reader.onerror = reject;
          reader.readAsDataURL(rawFile);
        })
    );

    try {
      await Promise.all(filePromises);
      onChange(newFiles);
    } catch {
      enqueueSnackbar("Fehler bei der Verarbeitung der hochgeladenen Dateien!", {
        variant: "error",
      });
    }
  };

  const removeFile = (itemToRemove) => {
    onChange(files.filter((f) => f !== itemToRemove));
  };

  const showDocument = (file) => {
    const doc = {
      success: true,
      filename: file.filename,
      filesize: file.filesize,
      filetype: file.filetype,
      mimetype: file.mimetype,
      filedata: file.filedata,
      label: file.label || file.filename,
    };
    setDocumentToView(null);
    setTimeout(() => setDocumentToView(doc), 0);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragging(false);
      processFiles(Array.from(e.dataTransfer.files));
    }
  };

  const accept = allowedExtensions.length
    ? allowedExtensions.map((e) => `.${e.toLowerCase()}`).join(",")
    : undefined;

  return (
    <>
      {label && (
        <Typography
          variant="body2"
          sx={{
            mb: 1,
            color: error ? "error.main" : "text.secondary",
            fontSize: typographyTokens.size.sm,
          }}
        >
          {label}
        </Typography>
      )}

      <Box
        sx={{
          border: `1px dashed ${dragging ? componentTokens.uploadDropzone.border.drag : componentTokens.uploadDropzone.border.default}`,
          borderRadius: componentTokens.uploadDropzone.borderRadius,
          padding: componentTokens.uploadDropzone.padding,
          textAlign: "center",
          minHeight: "100px",
          ":hover": {
            cursor: disabled ? "default" : "pointer",
            backgroundColor: disabled ? "lightgray" : "clickableHover",
          },
          backgroundColor: disabled
            ? componentTokens.uploadDropzone.background.disabled
            : dragging
              ? componentTokens.uploadDropzone.background.dragging
              : componentTokens.uploadDropzone.background.default,
          opacity: disabled ? 0.6 : 1,
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <Box sx={{ mb: dimensions["md"] }}>
          <UploadIcon />
        </Box>
        <Box sx={{ mb: dimensions["md"] }}>
          Datei hier ablegen
          <br />
          {dragging ? "... " : " oder "}
        </Box>
        <DSButton
          color="primary"
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) fileInputRef.current?.click();
          }}
          sx={{ opacity: dragging ? 0.1 : 1, mb: dimensions["lg"] }}
          disabled={disabled}
        >
          Auswählen
        </DSButton>

        {allowedExtensions.length > 0 && (
          <Typography
            component="div"
            sx={{
              fontSize: typographyTokens.size.sm,
              fontWeight: typographyTokens.weight?.regular,
              color: componentTokens.uploadDropzone.text.color,
              maxWidth: "75%",
              mx: "auto",
              textWrap: "balance",
              mb: dimensions["md"],
            }}
          >
            <p>Maximale Dateigröße: 15 MB</p>
            <p>Zulässige Dateitypen: {allowedExtensions.join(", ")}</p>
          </Typography>
        )}

        <input
          type="file"
          multiple={multiple}
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={(e) => processFiles(Array.from(e.target.files))}
          accept={accept}
          disabled={disabled}
        />
      </Box>

      {files.length > 0 && (
        <Box sx={{ mt: "20px", pl: "0px", pr: "0px" }}>
          {files.map((item, index) => (
            <Box
              key={item.id || index}
              sx={{
                mt: "5px",
                paddingRight: componentTokens.listItem.paddingRight,
                paddingTop: componentTokens.listItem.paddingY,
                paddingBottom: componentTokens.listItem.paddingY,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontSize: typographyTokens.size.sm,
                  display: "flex",
                  alignItems: "center",
                  gap: dimensions["2xl"],
                  minWidth: 0,
                  flex: 1,
                }}
              >
                {getFileIcon(item.filetype)}
                {formatFileSize(item.filesize)}
                <Tooltip title={item.filename}>
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {item.filename}
                  </Box>
                </Tooltip>
              </Typography>

              <Box sx={{ display: "flex" }}>
                <DSButton
                  elevation={0}
                  sx={{
                    height: componentTokens.listItem.actionButton.size,
                    minWidth: componentTokens.listItem.actionButton.size,
                    width: componentTokens.listItem.actionButton.size,
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor:
                        componentTokens.listItem.actionButton.remove.hoverBackground,
                      boxShadow: "none",
                    },
                  }}
                  disabled={disabled}
                  onClick={() => removeFile(item)}
                >
                  <DeleteIcon
                    sx={{ color: componentTokens.listItem.actionButton.remove.iconColor }}
                  />
                </DSButton>
                &nbsp;
                <DSButton
                  sx={{
                    height: componentTokens.listItem.actionButton.size,
                    minWidth: componentTokens.listItem.actionButton.size,
                    width: componentTokens.listItem.actionButton.size,
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor:
                        componentTokens.listItem.actionButton.preview.hoverBackground,
                      boxShadow: "none",
                    },
                  }}
                  disabled={disabled}
                  onClick={() => showDocument(item)}
                >
                  <PreviewIcon
                    sx={{ color: componentTokens.listItem.actionButton.preview.iconColor }}
                  />
                </DSButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {error && <FormHelperText error>{error.message}</FormHelperText>}

      <DocumentViewer key={documentToView?.id} document={documentToView} />
    </>
  );
};

FormDnDFileUpload.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  allowedExtensions: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
};

export default FormDnDFileUpload;
